import React from 'react'
import { UpdateStatusAction } from './update-status-action'
import { Library } from './action-list'
import { PureQueryOptions } from 'apollo-client'
import { UpdateApplicationLibraryStatusMutation } from '../../api/graphql/update-application-library-status-mutation'
import { logError, logSuccess } from '../../../../components/toaster'
import { I18nConsumer } from '../../../../components/i18n-provider'
import { UpdateApplicationLibraryStatusByFiltersMutation } from '../../api/graphql/update-application-library-status-filters-mutation'
import { DeleteApplicationLibraryStatusMutation } from '../../api/graphql/delete-application-library-status-mutation'
import { DeleteApplicationLibraryStatusByFiltersMutation } from '../../api/graphql/delete-application-library-status-filters-mutation'
import { LibraryStatus } from '../cell-content/status-cell'

interface UpdateStatusesWithHashesActionProps {
  organizationID: string
  libraries: Library[]
  applicationID: string
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any> | (() => void)
}

export function UpdateStatusesWithHashesAction({
  organizationID,
  libraries,
  applicationID,
  refetchQueries,
  refresh
}: UpdateStatusesWithHashesActionProps) {
  return (
    <DeleteApplicationLibraryStatusMutation>
      {(deleteStatusesMutation) => (
        <UpdateApplicationLibraryStatusMutation>
          {(updateStatusesMutation) => (
            <I18nConsumer>
              {({ intl }) => (
                <UpdateStatusAction
                  menuPlacement="top"
                  updateStatuses={async (status) => {
                    if (status) {
                      try {
                        // eslint-disable-next-line unicorn/prefer-ternary
                        if (status !== LibraryStatus.NONE) {
                          await updateStatusesMutation(
                            {
                              organizationID,
                              applicationID,
                              hashes: libraries.map((library) => library.hash),
                              status: status.name
                            },
                            refetchQueries ? refetchQueries : []
                          )
                        } else {
                          await deleteStatusesMutation(
                            {
                              organizationID,
                              applicationID,
                              hashes: libraries.map((library) => library.hash)
                            },
                            refetchQueries ? refetchQueries : []
                          )
                        }
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        if (refresh) {
                          await refresh()
                        }
                        logSuccess(
                          intl.formatMessage(
                            {
                              id: 'detail_libraries_successfullyUpdatedLibraryStatus'
                            },
                            { count: libraries.length }
                          )
                        )
                      } catch (error) {
                        logError(
                          intl.formatMessage(
                            {
                              id: 'detail_libraries_failedToUpdateLibraryStatus'
                            },
                            { count: libraries.length }
                          )
                        )
                      }
                    }
                  }}
                />
              )}
            </I18nConsumer>
          )}
        </UpdateApplicationLibraryStatusMutation>
      )}
    </DeleteApplicationLibraryStatusMutation>
  )
}

interface UpdateStatusesWithFilterActionProps {
  organizationID: string
  applicationID: string
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  excludedHashes: string[]
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any> | (() => void)
}

export function UpdateStatusesWithFilterAction({
  organizationID,
  applicationID,
  filters: { aggregations, filterText, quickFilter },
  excludedHashes,
  refetchQueries,
  refresh
}: UpdateStatusesWithFilterActionProps) {
  return (
    <DeleteApplicationLibraryStatusByFiltersMutation>
      {(deleteStatusesMutation) => (
        <UpdateApplicationLibraryStatusByFiltersMutation>
          {(updateStatusesMutation) => (
            <I18nConsumer>
              {({ intl }) => (
                <UpdateStatusAction
                  menuPlacement="top"
                  updateStatuses={async (status) => {
                    if (status && refetchQueries) {
                      try {
                        // eslint-disable-next-line unicorn/prefer-ternary
                        if (status !== LibraryStatus.NONE) {
                          await updateStatusesMutation(
                            {
                              organizationID,
                              applicationID,
                              filters: Object.assign({}, aggregations, {
                                filterText,
                                quickFilter
                              }),
                              excludedHashes,
                              status: status.name
                            },
                            refetchQueries
                          )
                        } else {
                          await deleteStatusesMutation(
                            {
                              organizationID,
                              applicationID,
                              filters: Object.assign({}, aggregations, {
                                filterText,
                                quickFilter
                              }),
                              excludedHashes
                            },
                            refetchQueries
                          )
                        }
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        if (refresh) {
                          await refresh()
                        }
                        logSuccess(
                          intl.formatMessage(
                            {
                              id: 'detail_libraries_successfullyUpdatedLibraryStatus'
                            },
                            { count: 2 }
                          )
                        )
                      } catch (error) {
                        console.error(error)
                        logError(
                          intl.formatMessage(
                            {
                              id: 'detail_libraries_failedToUpdateLibraryStatus'
                            },
                            { count: 2 }
                          )
                        )
                      }
                    }
                  }}
                />
              )}
            </I18nConsumer>
          )}
        </UpdateApplicationLibraryStatusByFiltersMutation>
      )}
    </DeleteApplicationLibraryStatusByFiltersMutation>
  )
}
